import { Icon } from "@iconify/react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import MapLoadScreen from "./mapLoadScreen.jsx";

const NearbyStores = ({ product }) => {
  const [open, setOpen] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [error, setError] = useState(null);
  const [address, setAddress] = useState(null);
  const [addressCity, setAddressCity] = useState(null);
  const [zipcode, setZipcode] = useState("");
  const [zipError, setZipError] = useState("");
  const [useManualLocation, setUseManualLocation] = useState(false);
  const [showZipInput, setShowZipInput] = useState(false);
  const [readyToFetch, setReadyToFetch] = useState(false);

  const validateZipcode = (zip) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(zip)) {
      setZipError("Enter a valid ZIP code");
      return false;
    }
    setZipError("");
    return true;
  };

  const handleZipChange = (e) => {
    setZipcode(e.target.value);
    if (zipError) validateZipcode(e.target.value);
  };

  const handleZipSubmit = () => {
    if (validateZipcode(zipcode)) {
      setUseManualLocation(true);
      setShowZipInput(false);
      setAddressCity(zipcode);
      setReadyToFetch(true);
    }
  };

  const getGeolocation = () => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            resolve({ latitude, longitude });
          },
          (err) => reject(err),
          { timeout: 10000 }
        );
      } else {
        reject(new Error("Geolocation is not supported by this browser."));
      }
    });
  };

  const reverseGeocode = async ({ latitude, longitude }) => {
    try {
      const response = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            latlng: `${latitude},${longitude}`,
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          },
        }
      );
      const data = response.data;
      if (data.results && data.results.length > 0) {
        const components = data.results[0].address_components;
        let postalCode = null;
        let city = null;
        components.forEach((component) => {
          if (component.types.includes("postal_code") && !postalCode) {
            postalCode = component.long_name;
          }
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
        });
        return { postalCode: postalCode || city || null };
      }
      return null;
    } catch (err) {
      console.error("Reverse geocoding error:", err);
      return null;
    }
  };

  useEffect(() => {
    if (open) {
      setStores([]);
      setSelectedStore(null);
      setLoading(true);
      setError(null);
      setAddress(null);
      setAddressCity(null);
      setLocation({ latitude: null, longitude: null });
      setLocationLoading(false);
      setReadyToFetch(false);

      if (!useManualLocation) {
        getGeolocation()
          .then((pos) => {
            setLocation(pos);
            return reverseGeocode(pos);
          })
          .then((result) => {
            if (result && result.postalCode) {
              setAddressCity(result.postalCode);
              setAddress(result.postalCode);
              setReadyToFetch(true);
            } else {
              setAddress("No address found.");
            }
            setLocationLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setError("Geolocation error: " + err.message);
            setLocationLoading(false);
            if (err.code === 1) {
              setUseManualLocation(true);
              setShowZipInput(true);
            }
          });
      }
    } else {
      setStores([]);
      setSelectedStore(null);
      setLoading(true);
      setError(null);
      setAddressCity(null);
      setLocation({ latitude: null, longitude: null });
      setShowZipInput(false);
      setReadyToFetch(false);
    }
  }, [open]);

  const calculateDistance = useCallback(
    (lat1, lon1) => {
      if (!location.latitude || !location.longitude) return null;
      const deltaLat = Math.abs(location.latitude - lat1);
      const deltaLon = Math.abs(location.longitude - lon1);
      const R = 69.1; // Rough conversion: miles per degree
      const latDistance = deltaLat * R;
      const lonDistance = deltaLon * R * Math.cos(lat1 * (Math.PI / 180));
      const distance = latDistance + lonDistance;
      return distance.toFixed(2);
    },
    [location]
  );

  const fetchCoordinates = async (storeAddress) => {
    try {
      const response = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address: storeAddress,
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
          },
        }
      );
      if (response.data.status === "OK") {
        const loc = response.data.results[0].geometry.location;
        return { lat: loc.lat, lng: loc.lng };
      } else {
        console.error("Geocoding error:", response.data.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  const fetchStores = async () => {
    if (!product) return;
    if (!addressCity) {
      console.error("No address available to fetch stores.");
      setLoading(false);
      return;
    }
    try {
      const response = await axios.post(
        "/get_nearby_availability",
        { product, addressCity },
        { headers: { "Content-Type": "application/json" } }
      );
      if (response.status === 404) {
        setStores([]);
      }
      const formattedStores = await Promise.all(
        response.data.map(async (place, index) => {
          const coords = await fetchCoordinates(place.address);
          return {
            id: index,
            name: place.name,
            address: place.address,
            ...(coords && { lat: coords.lat, lng: coords.lng }),
            distanceAway: coords ? calculateDistance(coords.lat, coords.lng) : null,
          };
        })
      );
      const sortedStores = formattedStores.sort((a, b) => {
        return (
          parseFloat(a.distanceAway || Infinity) -
          parseFloat(b.distanceAway || Infinity)
        );
      });
      setStores(sortedStores);
      if (sortedStores.length > 0) {
        setSelectedStore(sortedStores[0]);
      }
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && addressCity && product && readyToFetch) {
      fetchStores();
    }
  }, [open, addressCity, product, readyToFetch, calculateDistance]);

  const handleStoreClick = (store) => {
    setSelectedStore(store);
    console.log("Selected store:", store);
  };

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setUseManualLocation(false);
    setShowZipInput(false);
    setZipcode("");
    setZipError("");
  };

  if (open && useManualLocation && showZipInput) {
    return (
      <>
        <IconButton
          onClick={handleClick}
          sx={{
            width: "194px",
            height: "53px",
            borderRadius: "30px",
            background: "#E8E8E8",
            gap: 1,
            "&:hover": { background: "#E8E8E8", opacity: 1 },
          }}
        >
          <Icon icon={"bx:store"} style={{ fontWeight: 800, color: "#205375" }} />
          <Typography variant="h6" sx={{ color: "#205375" }}>
            Store Nearby
          </Typography>
        </IconButton>

        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              padding: 4,
              width: "400px",
              background: "white",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Enter Location
            </Typography>
            <Typography variant="body2" sx={{ textAlign: "center", mb: 2 }}>
              Please enter a ZIP code to find nearby stores.
            </Typography>
            <TextField
              variant="outlined"
              size="medium"
              label="Enter ZIP Code"
              value={zipcode}
              onChange={handleZipChange}
              error={!!zipError}
              helperText={zipError}
              sx={{ width: "100%", mb: 2 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !zipError && zipcode) {
                  handleZipSubmit();
                }
              }}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="outlined" color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleZipSubmit}
                disabled={!zipcode || !!zipError}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          width: "194px",
          height: "53px",
          borderRadius: "30px",
          background: "#E8E8E8",
          gap: 1,
          "&:hover": { background: "#E8E8E8", opacity: 1 },
        }}
      >
        <Icon icon={"bx:store"} style={{ fontWeight: 800, color: "#205375" }} />
        <Typography variant="h6" sx={{ color: "#205375" }}>
          Store Nearby
        </Typography>
      </IconButton>

      {open && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "80vw",
              height: "80vh",
              background: '#E6F5FA',
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              padding: "16px",
              overflow: "hidden",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ display: "flex", flex: 1, height: "100%" }}>
              <Box
                sx={{
                  width: "30%",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: "16px", color: "#0E2B5D" }}>
                  Nearby Stores
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "16px",
                    marginBottom: "10px",
                  }}
                >
                  {loading ? (
                    <Typography variant="body2" sx={{ textAlign: "center" , color: "#154490"}}>
                      Loading nearby stores...
                    </Typography>
                  ) : stores.length > 0 ? (
                    stores.map((store, index) => (
                      <Box
                        key={index}
                        sx={{
                          background: selectedStore.id === store.id ? '#154490' :"white",
                          padding: "10px",
                          borderRadius: "8px",
                          marginBottom: "10px",
                          color: selectedStore.id === store.id ? 'white' :"#0E2B5D",
                          cursor: "pointer",
                          "&:hover": {
                            border: "2px solid #154490",
                          },
                        }}
                        onClick={() => handleStoreClick(store)}
                      >
                        <Typography variant="body1">
                          <strong>{store.name}</strong>
                        </Typography>
                        <Typography variant="body2">{store.address}</Typography>
                        {location.latitude &&
                          location.longitude &&
                          store.distanceAway && (
                            <Typography variant="body2">
                              {store.distanceAway} Miles away
                            </Typography>
                          )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ textAlign: "center" }}>
                      No nearby stores found.
                    </Typography>
                  )}
                </Box>
                <Button
                  onClick={handleClose}
                  style={{
                    backgroundColor: "#154490",
                    color: "white",
                    marginTop: "16px",
                    marginX: "4px",
                    width: "90%",
                  }}
                >
                  Close
                </Button>
              </Box>

              {/* Right Pane: Map */}
              <Box
                sx={{
                  width: "70%",
                  height: "100%",
                  background: "#e0e0e0",
                  borderRadius: "8px",
                  position: "relative",
                }}
              >
                {loading ? (
                  <MapLoadScreen />
                ) : stores.length === 0 ? (
                  <Typography variant="h6" align="center" sx={{ padding: 2 }}>
                    No stores found
                  </Typography>
                ) : (
                  selectedStore && (
                    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                      <GoogleMap
                        mapContainerStyle={{ height: "100%", width: "100%" }}
                        center={{ lat: selectedStore.lat, lng: selectedStore.lng }}
                        zoom={18}
                      >
                        <Marker position={{ lat: selectedStore.lat, lng: selectedStore.lng }} />
                      </GoogleMap>
                    </LoadScript>
                  )
                )}
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NearbyStores;